import type { TransactionItem } from '@/types/transactions';
import type { BillReceiptData } from '@/types/receipt';
import { formatReceiptDateTime } from '@/utils/receiptFormatters';

export function formatTransactionNaira(amount: number) {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 2,
  }).format(amount);
}

export function formatTransactionAmount(tx: TransactionItem) {
  const prefix = tx.type === 'credit' ? '+' : '-';
  return `${prefix}${formatTransactionNaira(tx.amount)}`;
}

export function getTransactionStatusTone(status: TransactionItem['status']) {
  switch (status) {
    case 'success':
      return 'bg-emerald-100 text-emerald-700';
    case 'pending':
      return 'bg-amber-100 text-amber-700';
    case 'failed':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

export function getCategoryTone(category: TransactionItem['category']) {
  switch (category) {
    case 'wallet':
      return 'bg-blue-100 text-blue-700';
    case 'airtime':
      return 'bg-sky-100 text-sky-700';
    case 'data':
      return 'bg-purple-100 text-purple-700';
    case 'bills':
      return 'bg-orange-100 text-orange-700';
    case 'verification':
      return 'bg-emerald-100 text-emerald-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

export function transactionToBillReceipt(tx: TransactionItem): BillReceiptData | null {
  const { date, time } = formatReceiptDateTime(new Date(tx.createdAt));
  const meta = tx.metadata;

  if (tx.kind === 'electricity_purchase') {
    return {
      type: 'electricity',
      reference: tx.reference,
      amount: Number(meta.faceValue ?? tx.faceValue ?? tx.amount),
      amountPaid: tx.amount,
      providerName: tx.serviceName,
      date,
      time,
      meterNumber: String(meta.meterNumber ?? ''),
      meterType: (meta.meterType as 'prepaid' | 'postpaid') ?? 'prepaid',
      customerName: String(meta.customerName ?? tx.description ?? ''),
      token: meta.token ? String(meta.token) : null,
      units: meta.units ? String(meta.units) : null,
    };
  }

  if (tx.kind === 'exam_purchase') {
    const examPins = Array.isArray(meta.examPins)
      ? (meta.examPins as Array<{ serial: string; pin: string }>)
      : undefined;
    return {
      type: 'exam',
      reference: tx.reference,
      amount: tx.amount,
      amountPaid: tx.amount,
      providerName: tx.serviceName,
      date,
      time,
      planName: String(meta.planName ?? tx.serviceName),
      candidateName: String(meta.candidateName ?? meta.profileId ?? ''),
      phone: String(meta.phone ?? ''),
      profileId: String(meta.profileId ?? ''),
      purchasedCode: meta.purchasedCode ? String(meta.purchasedCode) : null,
      examPins,
    };
  }

  return null;
}
